import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { PenSquare, Library, CalendarDays, Zap, Settings, Footprints, Home as HomeIcon } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useTrialExhaustedDialog } from "@/hooks/use-trial-exhausted-dialog";
import { isTrialExhausted } from "@/lib/trial";
import { BrandLockup } from "@/components/brand-lockup";

export function Layout({ children, hideNav = false }: { children: React.ReactNode; hideNav?: boolean }) {
  const [location] = useLocation();
  const { user } = useAuth();
  const { trialExhaustedDialog, openTrialExhaustedFromProfile } = useTrialExhaustedDialog();
  const exhausted = isTrialExhausted(user);

  return (
    <div className="min-h-screen flex flex-col relative">
      {!hideNav && (
        <header className="sticky top-0 z-50 w-full border-b border-foreground/[0.07] bg-[rgba(248,246,242,0.82)] backdrop-blur-xl">
          <div className="w-full px-6 md:px-8 lg:px-12 xl:px-16 h-16 relative flex items-center">
            {/* Logo: left-aligned */}
            <Link
              href="/"
              className="flex items-center group"
            >
              <BrandLockup />
            </Link>

            <nav className="hidden md:flex items-center gap-1 ml-8">
              <Link
                href="/"
                className={cn(
                  "text-sm font-medium transition-colors flex items-center gap-1.5 h-9 px-3 rounded-[18px]",
                  location === "/"
                    ? "bg-primary/10 text-primary"
                    : "text-ink-soft hover:text-foreground hover:bg-foreground/[0.05]",
                )}
              >
                <HomeIcon className="w-3.5 h-3.5" />
                Home
              </Link>
              <Link
                href="/unfoldings"
                className={cn(
                  "text-sm font-medium transition-colors flex items-center gap-1.5 h-9 px-3 rounded-[18px]",
                  location === "/unfoldings"
                    ? "bg-primary/10 text-primary"
                    : "text-ink-soft hover:text-foreground hover:bg-foreground/[0.05]",
                )}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Unfoldings
              </Link>
              <Link
                href="/momentum"
                className={cn(
                  "text-sm font-medium transition-colors flex items-center gap-1.5 h-9 px-3 rounded-[18px]",
                  location === "/momentum"
                    ? "bg-primary/10 text-primary"
                    : "text-ink-soft hover:text-foreground hover:bg-foreground/[0.05]",
                )}
              >
                <Footprints className="w-3.5 h-3.5" />
                Momentum
              </Link>
              <Link
                href="/library"
                className={cn(
                  "text-sm font-medium transition-colors flex items-center gap-1.5 h-9 px-3 rounded-[18px]",
                  location === "/library"
                    ? "bg-primary/10 text-primary"
                    : "text-ink-soft hover:text-foreground hover:bg-foreground/[0.05]",
                )}
              >
                <Library className="w-3.5 h-3.5" />
                Library
              </Link>
            </nav>

            <div className="flex items-center gap-2 md:gap-4 ml-auto">
              {/* Mobile primary action: New Unfolding lives in the header on mobile, so the
                  bottom bar holds only destinations. Same /app route and trial-exhausted
                  branch as the desktop button below. */}
              {exhausted ? (
                <button
                  type="button"
                  aria-label="Start a new Unfolding"
                  onClick={() => openTrialExhaustedFromProfile(user)}
                  className="md:hidden w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 bg-amber-100 text-amber-900 border border-amber-200 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <PenSquare className="w-5 h-5" />
                </button>
              ) : (
                <Link
                  href="/app"
                  aria-label="Start a new Unfolding"
                  className={cn(
                    "md:hidden w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 text-white shadow-sm transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    location === "/app" ? "bg-primary/90" : "bg-primary",
                  )}
                >
                  <PenSquare className="w-5 h-5" />
                </Link>
              )}

              {!exhausted && (
                <Link
                  href="/pricing"
                  className="hidden md:flex items-center gap-1.5 h-7 px-3 rounded-[14px] text-xs font-semibold text-primary bg-primary/8 hover:bg-primary/14 transition-colors"
                >
                  <Zap className="w-3 h-3" />
                  Upgrade
                </Link>
              )}

              <Link
                href="/settings"
                className={cn(
                  "hidden md:flex w-8 h-8 -mx-2 rounded-full items-center justify-center transition-colors",
                  location === "/settings"
                    ? "bg-primary/10 text-primary"
                    : "text-ink-soft hover:text-foreground hover:bg-foreground/[0.05]",
                )}
                title="Settings"
              >
                <Settings className="w-4 h-4" />
              </Link>

              {/* New Unfolding: icon-only on desktop so the primary action reads as a
                  compact peer of the gear rather than dominating the nav. The label
                  moves into a tooltip (hover + keyboard focus) and stays on the
                  element as aria-label. When the trial is over it explains via the
                  dialog instead of navigating. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  {exhausted ? (
                    <button
                      type="button"
                      aria-label="New Unfolding"
                      onClick={() => openTrialExhaustedFromProfile(user)}
                      className={cn(
                        "hidden md:flex w-9 h-9 rounded-full items-center justify-center transition-all shadow-sm shrink-0",
                        "bg-amber-100 text-amber-900 hover:bg-amber-200/90 border border-amber-200",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                      )}
                    >
                      <PenSquare className="w-4 h-4" />
                    </button>
                  ) : (
                    <Link
                      href="/app"
                      aria-label="New Unfolding"
                      className="hidden md:flex w-9 h-9 rounded-full items-center justify-center transition-all shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)] shrink-0 bg-foreground text-background hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      <PenSquare className="w-4 h-4" />
                    </Link>
                  )}
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs font-medium">
                  New Unfolding
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </header>
      )}

      {trialExhaustedDialog}

      <main className="flex-1 flex flex-col w-full relative z-10 pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile bottom tab nav — floating Apple-style liquid glass pill */}
      {!hideNav && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-3 pb-safe pointer-events-none">
          <div
            className="pointer-events-auto mx-auto max-w-sm mb-2 rounded-[28px] flex items-center justify-around px-2 py-2"
            style={{
              background: "rgba(255,255,255,0.62)",
              backdropFilter: "blur(26px) saturate(185%)",
              WebkitBackdropFilter: "blur(26px) saturate(185%)",
              border: "1px solid rgba(255,255,255,0.75)",
              boxShadow: "0 10px 34px rgba(28,25,23,0.18), inset 0 1px 0 rgba(255,255,255,0.85)",
            }}
          >
            <MobileTab href="/" icon={<HomeIcon className="w-6 h-6" />} label="Home" active={location === "/"} />
            <MobileTab href="/unfoldings" icon={<CalendarDays className="w-6 h-6" />} label="Unfoldings" active={location === "/unfoldings"} />
            <MobileTab href="/momentum" icon={<Footprints className="w-6 h-6" />} label="Momentum" active={location === "/momentum"} />
            <MobileTab href="/library" icon={<Library className="w-6 h-6" />} label="Library" active={location === "/library"} />
            <MobileTab href="/settings" icon={<Settings className="w-6 h-6" />} label="Settings" active={location === "/settings"} />
          </div>
        </nav>
      )}
    </div>
  );
}

function MobileTab({
  href,
  icon,
  label,
  active,
  primary,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  primary?: boolean;
}) {
  return (
    <Link href={href} aria-label={label} className="flex items-center justify-center min-w-[52px]">
      <div
        className={cn(
          "w-11 h-11 rounded-2xl flex items-center justify-center transition-all",
          primary
            ? "bg-primary text-white shadow-sm"
            : active
              ? "bg-primary/15 text-primary"
              : "text-stone-500",
        )}
      >
        {icon}
      </div>
    </Link>
  );
}
